import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { DeepLinkUtil } from '@edtech-ui/core/utils/deep-link.util';

@Component({
	selector: 'app-public-interview-list',
	standalone: true,
	imports: [CommonModule, RouterModule, FormsModule],
	templateUrl: './public-interview-list.component.html',
	styleUrl: './public-interview-list.component.scss'
})
export class PublicInterviewListComponent implements OnInit, OnDestroy {

	isBrowser: boolean;
	isDashboard = false;

	mockInterviews: any[] = [];
	searchTerm = '';
	selectedCategory = 'All';
	loading = false;
	isLoadingMore = false;
	hasMore = false;
	page = 1;
	pageSize = 9;
	totalRecords = 0;

	private destroy$ = new Subject<void>();
	private searchTrigger$ = new Subject<void>();

	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		private router: Router,
		private http: HttpClient,
		private toast: ToastrService
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	ngOnInit(): void {
		if (this.isBrowser) {
			const token = localStorage.getItem('token');
			if (token && !this.router.url.includes('student-dashboard')) {
				this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
				return;
			}
			this.isDashboard = !!token;
		}

		this.setupSearchTrigger();
		this.fetchMockInterviews(true);
	}

	setupSearchTrigger(): void {
		this.searchTrigger$
			.pipe(debounceTime(400), takeUntil(this.destroy$))
			.subscribe(() => {
				this.fetchMockInterviews(true);
			});
	}

	fetchMockInterviews(isInitial = true): void {
		if (isInitial) {
			this.loading = true;
			this.page = 1;
			this.mockInterviews = [];
		} else {
			this.isLoadingMore = true;
		}

		let url = `${environment.hrmsApiUrl}/rounds?is_public=true&publish_status=Published&page=${this.page}&limit=${this.pageSize}`;

		if (this.selectedCategory && this.selectedCategory !== 'All') {
			url += '&interview_category=' + encodeURIComponent(this.selectedCategory);
		}

		if (this.searchTerm?.trim()) {
			url += '&search=' + encodeURIComponent(this.searchTerm.trim());
		}

		this.http.get<any>(url)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					const dataObj = res?.Data || res?.data;
					const newRounds = dataObj && Array.isArray(dataObj.rounds) ? dataObj.rounds : [];

					if (isInitial) {
						this.mockInterviews = newRounds;
					} else {
						this.mockInterviews = [...this.mockInterviews, ...newRounds];
					}

					this.totalRecords = dataObj?.pagination?.total ?? dataObj?.total ?? this.mockInterviews.length;

					if (dataObj?.pagination) {
						this.hasMore = this.mockInterviews.length < this.totalRecords;
					} else {
						this.hasMore = newRounds.length >= this.pageSize;
					}

					this.loading = false;
					this.isLoadingMore = false;
				},
				error: (err) => {
					console.error('Failed to fetch mock interviews', err);
					this.loading = false;
					this.isLoadingMore = false;
					if (isInitial) {
						this.mockInterviews = [];
						this.totalRecords = 0;
					}
					this.toast.error('Failed to load practice interviews');
				}
			});
	}

	loadMore(): void {
		if (!this.hasMore || this.isLoadingMore) return;
		this.page++;
		this.fetchMockInterviews(false);
	}

	onSearchChange(): void {
		this.searchTrigger$.next();
	}

	onCategoryChange(): void {
		this.fetchMockInterviews(true);
	}

	clearFilters(): void {
		this.searchTerm = '';
		this.selectedCategory = 'All';
		this.fetchMockInterviews(true);
	}

	startMockInterview(roundId: string): void {
		if (!roundId) {
			this.toast.error('Invalid interview selected');
			return;
		}

		// Deep Linking: Try to launch the Android app into the interview dashboard
		// rather than proceeding with web navigation for mobile users.
		if (DeepLinkUtil.isAndroid()) {
			window.location.href = DeepLinkUtil.getAndroidIntentUrl('student-dashboard/ai-interview-dashboard');
			return;
		}

		const token = this.isBrowser ? localStorage.getItem('token') : null;
		if (!token) {
			if (this.isBrowser) {
				localStorage.setItem('pending_practice_round_id', roundId);
			}
			this.router.navigate(['/login'], { queryParams: { redirectUrl: '/student-dashboard/ai-interview-dashboard' } });
			return;
		}

		const interview = this.mockInterviews.find(i => i.id === roundId || i._id === roundId || i.screening_round_id === roundId);
		if (!interview) {
			this.toast.error('Interview not found');
			return;
		}

		let uniqueToken = interview.unique_token;
		if (!uniqueToken && interview.interview_url) {
			const parts = interview.interview_url.split('/');
			if (parts.length >= 2) {
				uniqueToken = parts[parts.length - 2];
			}
		}

		if (uniqueToken) {
			const urlTree = this.router.createUrlTree(['/interview.ai', uniqueToken, roundId], { queryParams: { is_b2c_practice: 'true' } });
			const url = this.router.serializeUrl(urlTree);
			window.open(url, '_blank');
		} else {
			this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
